import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Play, Loader2, AlertCircle, Workflow } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ErrorState } from "@/components/PageState";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { STATUS_STYLES, fmtDate, fmtDuration } from "@/lib/jobs";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const restricted = !!user?.restricted;
  const [job, setJob] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data.job);
      setRuns(data.runs);
    } catch (e) {
      setJob(null);
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load this job.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/jobs/${id}/run`);
      toast[data.status === "failed" ? "error" : "success"](`Run ${data.status}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to run job");
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!job) {
    return (
      <ErrorState
        title="Job not found"
        description={loadError || "It may have been deleted."}
        onRetry={load}
        actionTo="/dashboard/jobs"
        actionLabel="Back to jobs"
        testid="job-detail-error"
      />
    );
  }

  const failedRuns = runs.filter((r) => r.status !== "success" && (r.error_log || []).length);

  return (
    <div data-testid="job-detail-page">
      <Link
        to="/dashboard/jobs"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        data-testid="job-detail-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back to jobs
      </Link>

      <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Workflow className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl font-bold tracking-tight lg:text-3xl" data-testid="job-detail-name">
                {job.name}
              </h1>
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${STATUS_STYLES[job.status] || STATUS_STYLES.idle}`}
              >
                {job.status}
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {job.system_name} · <span className="capitalize">{job.frequency}</span> schedule
            </p>
          </div>
        </div>
        <Button onClick={runNow} disabled={restricted || running} className="gap-2 font-semibold" data-testid="job-detail-run">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          Run now
        </Button>
      </div>

      <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { label: "Last run", value: fmtDate(job.last_run_at) },
          { label: "Next run", value: fmtDate(job.next_run_at) },
          { label: "Total runs", value: runs.length },
          { label: "Failed runs", value: runs.filter((r) => r.status === "failed").length },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{s.label}</p>
            <p className="mt-1 font-display text-lg font-bold tracking-tight">{s.value}</p>
          </div>
        ))}
      </div>

      {failedRuns.length > 0 && (
        <div className="mb-8 rounded-xl border border-red-500/30 bg-red-500/5 p-5" data-testid="job-error-summary">
          <div className="mb-3 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
            <p className="font-display text-sm font-bold">Recent errors</p>
          </div>
          <ul className="space-y-2">
            {failedRuns.slice(0, 3).map((r) => (
              <li key={r.id} className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{fmtDate(r.started_at)}</span>
                {(r.error_log || []).map((e, i) => (
                  <span key={i} className="ml-2 block pl-4 text-red-600 dark:text-red-400">• {e}</span>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}

      <h2 className="mb-3 font-display text-lg font-bold">Run history</h2>
      <div className="overflow-hidden rounded-xl border border-border" data-testid="job-runs-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Started</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Records</th>
              <th className="px-4 py-3">Duration</th>
              <th className="px-4 py-3">Trigger</th>
              <th className="px-4 py-3">Details</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  No runs yet.
                </td>
              </tr>
            ) : (
              runs.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`job-run-row-${r.id}`}>
                  <td className="px-4 py-3">{fmtDate(r.started_at)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${STATUS_STYLES[r.status] || STATUS_STYLES.idle}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">{(r.records || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">{fmtDuration(r.duration_ms)}</td>
                  <td className="px-4 py-3 capitalize text-muted-foreground">{r.trigger}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {(r.error_log || []).length ? (
                      <span className="text-red-600 dark:text-red-400">{r.error_log.join("; ")}</span>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
